import { jsPDF } from "jspdf";
import { PDFDocument } from "pdf-lib";
import { pdf } from "@react-pdf/renderer";
import type { ReactElement } from "react";
import { loadImage } from "./fileUtils";

// export async function downloadJSPdf(doc: jsPDF, fileName: string): Promise<void> {

// }

export async function mergeJsPDFs(docs: jsPDF[]): Promise<Uint8Array> {
  const mergedPdf = await PDFDocument.create();

  for (const doc of docs) {
    const pdfBytes = doc.output("arraybuffer");
    const src = await PDFDocument.load(pdfBytes);
    const copiedPages = await mergedPdf.copyPages(src, src.getPageIndices());
    copiedPages.forEach((p) => mergedPdf.addPage(p));
  }

  return await mergedPdf.save();
}

export async function mergePdfBuffers(
  buffers: (ArrayBufferLike | Uint8Array)[],
): Promise<Uint8Array> {
  const merged = await PDFDocument.create();

  for (const buf of buffers) {
    const ab = toArrayBufferStrict(buf instanceof Uint8Array ? buf : (buf as ArrayBufferLike));
    const src = await PDFDocument.load(ab);
    const pages = await merged.copyPages(src, src.getPageIndices());
    pages.forEach((p) => merged.addPage(p));
  }

  return merged.save();
}

export function downloadPdfBytes(bytes: Uint8Array, fileName: string) {
  const blob = bytesToPdfBlob(bytes);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadMergedPDF(mergedBytes: Uint8Array, fileName = "merged.pdf") {
  // Create a Blob and object URL
  const blob = bytesToPdfBlob(mergedBytes);
  const url = URL.createObjectURL(blob);

  // Create a hidden link and click it programmatically
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function bytesToPdfFile(bytes: ArrayBufferLike | Uint8Array, fileName: string): File {
  const blob = bytesToPdfBlob(bytes);
  const safeName = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
  return new File([blob], safeName, { type: "application/pdf" });
}

export function jsPdfToArrayBufferLike(doc: jsPDF): ArrayBufferLike {
  return doc.output("arraybuffer") as ArrayBufferLike;
}

export async function renderReactPdfToArrayBufferLike(
  docElement: ReactElement,
): Promise<ArrayBufferLike> {
  const blob = await pdf(docElement).toBlob();
  return (await blob.arrayBuffer()) as ArrayBufferLike;
}

// --- Helpers --- //
// bytesNormalize.ts

/**
 * Normalize anything (ArrayBufferLike | Uint8Array) into a standalone ArrayBuffer.
 * Avoids .slice() on the underlying .buffer to prevent SharedArrayBuffer typing issues.
 */
function toArrayBufferStrict(buf: ArrayBufferLike | Uint8Array): ArrayBuffer {
  // Fast path: already a Uint8Array → copy to guarantee ArrayBuffer backing
  if (buf instanceof Uint8Array) {
    const copy = new Uint8Array(buf.byteLength);
    copy.set(buf); // copies bytes
    return copy.buffer as ArrayBuffer;
  }

  // Generic ArrayBufferLike (could be SAB) → copy via a view
  const view = new Uint8Array(buf);
  const copy = new Uint8Array(view.byteLength);
  copy.set(view);
  return copy.buffer as ArrayBuffer;
}

/** Turn bytes into a PDF Blob safely. */
export function bytesToPdfBlob(bytes: ArrayBufferLike | Uint8Array): Blob {
  const ab = toArrayBufferStrict(bytes instanceof Uint8Array ? bytes : (bytes as ArrayBufferLike));
  return new Blob([ab], { type: "application/pdf" });
}

// JS PDF helpers
type PreparePdfLogoArgs = {
  doc: jsPDF;
  printLogo?: string; // url or data url
  paperMargins: { top: number; bottom: number; left: number; right: number };
  targetMaxHeightPt?: number; // default 22
  yPos?: number; // default 16,
};

export async function preparePdfLogo({
  doc,
  printLogo,
  paperMargins,
  targetMaxHeightPt = 22,
  yPos = 16,
}: PreparePdfLogoArgs): Promise<null | {
  dataUrlPng: string; // ALWAYS png data url
  x: number;
  y: number;
  width: number;
  height: number;
}> {
  if (!printLogo) return null;
  const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
  const maxHeight = Math.min(targetMaxHeightPt, paperMargins.top - 3);
  const maxWidth = pageWidth - paperMargins.left - paperMargins.right;
  // 1) turn input into a PNG dataURL (rasterize if svg)
  const { dataUrlPng, imgWidth, imgHeight } = await toPngDataUrl(printLogo);
  // 2) measure rendered size
  let iWidth = imgWidth || maxWidth; // if we can’t measure, just fit to max
  let iHeight = imgHeight || maxHeight;
  let width = maxHeight * (iWidth / iHeight);
  let height = maxHeight;
  // const img = await loadImage(dataUrlPng);
  // const imgWidth = img.naturalWidth;
  // const imgHeight = img.naturalHeight;
  // let width = imgHeight > 0 ? maxHeight * (imgWidth / imgHeight) : 0;
  // let height = maxHeight;
  if (width > maxWidth) {
    width = maxWidth;
    height = iWidth > 0 ? width * (iHeight / iWidth) : 0;
  }
  if (height > pageHeight - paperMargins.top - paperMargins.bottom) {
    height = pageHeight - paperMargins.top - paperMargins.bottom;
    width = iHeight > 0 ? height * (iWidth / iHeight) : 0;
  }
  // place top-right-ish
  const x = pageWidth - paperMargins.right - width;
  const y = yPos;

  return { dataUrlPng, x, y, width, height };
}

function isSvgish(src: string) {
  return (
    src.startsWith("data:image/svg+xml") ||
    src.toLowerCase().includes(".svg") ||
    // some signed URLs don’t contain .svg clearly; if you store contentType, use that instead
    false
  );
}

export async function toPngDataUrl(
  src: string,
): Promise<{ dataUrlPng: string; imgWidth: number; imgHeight: number }> {
  // Already a raster data URL?
  if (src.startsWith("data:image/png") || src.endsWith(".png")) {
    const img = await loadImage(src);
    return { dataUrlPng: src, imgWidth: img.naturalWidth || 1, imgHeight: img.naturalHeight || 1 };
  }
  if (src.startsWith("data:image/jpeg") || src.endsWith(".jpg")) {
    const img = await loadImage(src);
    return { dataUrlPng: src, imgWidth: img.naturalWidth || 1, imgHeight: img.naturalHeight || 1 };
  }
  if (!isSvgish(src)) {
    // assume it’s a normal raster URL (png/jpg/webp) -> convert to dataUrl png
    const img = await loadImage(src);
    return {
      dataUrlPng: rasterImageToPngDataUrl(img),
      imgWidth: img.naturalWidth || 1,
      imgHeight: img.naturalHeight || 1,
    };
  }
  // SVG -> fetch text (works for URL or data URL)
  const svgText = src.startsWith("data:image/svg+xml")
    ? decodeSvgDataUrl(src)
    : await (await fetch(src, { mode: "cors" })).text();
  // Make sure the svg has xmlns, otherwise browser can fail to render
  const normalizedSvg = ensureSvgXmlns(svgText);
  // Turn SVG text into a blob URL, load it in <img>, draw to canvas -> png
  const blob = new Blob([normalizedSvg], { type: "image/svg+xml;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);

  try {
    const img = await loadImage(blobUrl);
    return {
      dataUrlPng: rasterImageToPngDataUrl(img),
      imgWidth: img.naturalWidth || 1,
      imgHeight: img.naturalHeight || 1,
    };
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

function rasterImageToPngDataUrl(img: HTMLImageElement): string {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || img.width || 256;
  canvas.height = img.naturalHeight || img.height || 256;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context not available");

  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL("image/png");
}

function decodeSvgDataUrl(dataUrl: string): string {
  // handles both base64 and utf8-encoded data urls
  const [, metaAndData] = dataUrl.split("data:image/svg+xml,");
  if (metaAndData !== undefined) return decodeURIComponent(metaAndData);

  const [, base64] = dataUrl.split("data:image/svg+xml;base64,");
  if (!base64) throw new Error("Invalid SVG data URL");
  return atob(base64);
}

function ensureSvgXmlns(svg: string): string {
  if (svg.includes('xmlns="http://www.w3.org/2000/svg"')) return svg;
  return svg.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
}

// type PaperMargins = {
//   top: number;
//   bottom: number;
//   left: number;
//   right: number;
// };

// type PreparePdfLogoProps = {
//   doc: jsPDF;
//   printLogo?: string | null;
//   paperMargins: PaperMargins;
//   targetMaxHeight?: number;
//   yPos?: number;
// };

// export async function preparePdfLogo({
//   doc,
//   printLogo,
//   paperMargins,
//   targetMaxHeight = 22,
//   yPos = 16,
// }: PreparePdfLogoProps) {
//   if (!printLogo) return null;

//   const img = await loadImage(printLogo);

//   const imgWidth = img.naturalWidth;
//   const imgHeight = img.naturalHeight;

//   const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
//   const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();

//   const maxWidth = pageWidth - paperMargins.left - paperMargins.right;
//   const maxHeight = Math.min(targetMaxHeight, paperMargins.top - 3);

//   let width = imgHeight > 0 ? maxHeight * (imgWidth / imgHeight) : 0;
//   let height = maxHeight;

//   if (width > maxWidth) {
//     width = maxWidth;
//     height = imgWidth > 0 ? width * (imgHeight / imgWidth) : 0;
//   }

//   if (height > pageHeight - paperMargins.top - paperMargins.bottom) {
//     height = pageHeight - paperMargins.top - paperMargins.bottom;
//     width = imgHeight > 0 ? height * (imgWidth / imgHeight) : 0;
//   }

//   const x = pageWidth - paperMargins.right - width;
//   const y = yPos;

//   return {
//     img,
//     x,
//     y,
//     width,
//     height,
//   };
// }
